export type Status = "OK" | "WARNING" | "CRITICAL";

export type Role = "ADMIN" | "MITARBEITER";

export interface Me { id: string; email: string; name: string; role: Role }

export interface UserRow {
  id: string; email: string; name: string; role: Role;
  createdAt: string; lastLoginAt: string | null;
}

export interface WearItem {
  partId: string; sku: string; name: string; group: string;
  scope: "SNR7" | "SNR12" | "ANY"; unit: "METER" | "HOUR";
  lifeValue: number; packSize: number; since: number; changedAt: string;
  fraction: number; remaining: number; remainingDays: number; status: Status;
}

export interface DeviceRow {
  id: string; inventoryNo: string; serialNo: string; model: string;
  crew: string; location: string; firmware: string;
  totalMeters: number; totalHours: number; runCount: number;
  lastSyncAt: string | null; nextInspectionAt: string;
  status: Status; reasons: string[]; metersPerDay: number;
  daysToInspection: number; worstWear: WearItem | null;
}

export interface DeviceDetail extends Omit<DeviceRow, "worstWear"> {
  commissionedAt: string; lastServiceAt: string | null;
  wear: WearItem[];
  protocols: { id: string; protocolNo: string; orderNo: string; ductType: string;
               cableType: string; lengthM: number; durationSec: number;
               maxForceN: number; maxPressureBar: number; startedAt: string }[];
  tickets: { id: string; ticketNo: string; serviceCode: string; state: string; note: string }[];
  history: { at: string; kind: "wear" | "service"; text: string; actor: string | null }[];
}

export interface PartRow {
  id: string; sku: string; name: string; group: string;
  scope: "SNR7" | "SNR12" | "ANY"; isWearPart: boolean; unit: "METER" | "HOUR";
  lifeValue: number | null; packSize: number; priceCents: number;
  stock: number; minStock: number; monthlyUse: number; imagePath: string | null;
  onOrder: number; perDay: number; available: number; reachDays: number;
  targetStock: number; suggested: number; status: Status;
}

export interface Dashboard {
  devices: { total: number; critical: number; warning: number; ok: number };
  openTickets: number;
  parts: { critical: number; suggestionValueCents: number };
  actions: { status: Status; deviceId: string; inventoryNo: string; crew: string; text: string }[];
  inspections: { id: string; inventoryNo: string; location: string;
                 nextInspectionAt: string; daysToInspection: number }[];
}

export interface OrderRow {
  id: string; orderNo: string; state: "PLACED" | "DELIVERED"; isAuto: boolean;
  placedAt: string; deliveredAt: string | null; totalCents: number;
  items: { partId: string; sku: string; name: string; qty: number;
           unitPriceCents: number; totalCents: number }[];
}

export const DEVICE_TYPES = [
  "AirJet 80", "AirJet 120", "AirJet 150", "AirJet 300", "AirJet 300 S",
  "AirJet 320 E", "AirJet 380 Automatic",
] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];

export interface CertificationRow {
  id: string; name: string; deviceType: DeviceType; trainingCode: string | null;
  validFrom: string; validUntil: string | null;
  filePath: string; fileName: string;
  crewName: string | null; deviceId: string | null;
  device: { id: string; inventoryNo: string } | null;
  training: { code: string; name: string } | null;
  createdAt: string;
}

export interface ShippingAddress { name: string; street: string; zip: string; city: string }

export interface ShippingLabel {
  ticketNo: string; labelTrackingNo: string; labelCreatedAt: string;
  recipient: ShippingAddress; sender: { name: string; location: string };
  device: { inventoryNo: string; serialNo: string };
}

export interface TicketRow {
  id: string; ticketNo: string; serviceCode: string; state: string; note: string;
  openedAt: string; device: { id: string; inventoryNo: string; crew: string };
  labelTrackingNo: string | null; labelCreatedAt: string | null;
  service: { code: string; name: string; priceCents: number; loaner: boolean } | null;
}

export interface ServiceCatalogEntry {
  code: string; name: string; priceCents: number; loaner: boolean; sortOrder: number;
  kind: "WARTUNG" | "REPARATUR" | "SCHULUNG";
  description: string | null; duration: string | null; credential: string | null;
}

export interface CrewRow { name: string; location: string; createdAt: string }

export interface AuditRow {
  id: string; at: string; actor: string; action: string; subject: string; detail: string;
}
